import type { ProjectSummary } from '@paid-ui/queries/search-projects';

import { forwardRef } from 'react';
import { useRouter } from 'next/router';
import { twMerge } from 'tailwind-merge';

import { ProjectCover } from '@paid-ui/components/project-cover';
import { Skeleton } from '@paid-ui/components/skeleton';
import { ProjectStatus } from '@paid-ui/enums/project';
import { BellIcon } from '@paid-ui/icons/bell';
import { ProjectIcon } from '@paid-ui/icons/project';
import { saveProjectSummary } from '@paid-ui/models/project';
import { formatCompactCurrency } from '@paid-ui/utils/formatter';
import { formatCreatedAt, formatProjectName, splitAddress } from '@paid-ui/utils/project';

import { NewProjectButton } from '../new-project-button';
import { getContractSummary, getPayingProgress, getReceivingProgress } from './utils';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  data?: ProjectSummary;
  loading?: boolean;
  badge?: number;
}

const cardClassName =
  'cursor-pointer rounded-lg bg-white shadow transition-shadow duration-150 ease-linear hover:shadow-lg';

const ClaimsBar: React.FC<{
  label: string;
  paid: number;
  total: number;
  ratio: number;
  barClassName: string;
}> = ({ label, paid, total, ratio, barClassName }) => (
  <div>
    <div className="flex items-baseline justify-between gap-x-2">
      <span className="text-sm leading-tight text-grey-dark">{label}</span>
      <span className="shrink-0 text-sm leading-tight">
        <span className="font-semibold">{formatCompactCurrency(paid)}</span>
        <span className="text-grey-dark"> of {formatCompactCurrency(total)}</span>
      </span>
    </div>
    <div className="mt-1 h-1 w-full overflow-hidden rounded-md bg-grey-mid">
      <div
        className={twMerge('h-full rounded-md', barClassName)}
        style={{ width: `${Math.round(ratio * 100)}%` }}
      />
    </div>
  </div>
);

export const ProjectCard = forwardRef<HTMLDivElement, Props>(
  ({ data, loading, badge = 0, className, ...restProps }, ref) => {
    const router = useRouter();

    if (loading) {
      return (
        <section ref={ref} className={twMerge(cardClassName, className)} {...restProps}>
          <div className="flex h-full w-full flex-col">
            <div className="relative h-28 w-full rounded-t-lg bg-grey-hover">
              <div className="absolute inset-x-0 bottom-0 px-4 pb-3.5">
                <Skeleton className="h-[20px] w-[50%]" />
              </div>
            </div>
            <div className="space-y-3 px-4 py-4">
              <div className="h-[42px] space-y-2">
                <Skeleton className="h-[16px]" />
                <Skeleton className="h-[12px] w-[45%]" />
              </div>
              <div className="h-[66px] space-y-2">
                <Skeleton className="h-[14px] w-[70%]" />
                <Skeleton className="h-[16px] w-[55%]" />
              </div>
              <div className="h-[54px] space-y-2">
                <Skeleton className="h-[14px]" />
                <Skeleton className="h-[14px]" />
              </div>
            </div>
          </div>
        </section>
      );
    }

    if (!data) {
      return (
        <section ref={ref} className={twMerge(cardClassName, className)} {...restProps}>
          <div className="flex h-28 w-full flex-col justify-center rounded-t-lg bg-grey-hover px-4 py-[18px]">
            <ProjectIcon className="h-[50px] w-[50px]" />
          </div>
          <div className="space-y-3 px-4 py-4">
            <h3 className="line-clamp-2 h-[42px] text-base font-medium leading-snug">
              Create your first project.
            </h3>
            <div className="h-[100px]" />
            <div className="h-[34px]">
              <NewProjectButton full />
            </div>
          </div>
        </section>
      );
    }

    const { total, executed, mix } = getContractSummary(data);
    const receiving = getReceivingProgress(data);
    const paying = getPayingProgress(data);
    const archived = data.projectStatus === ProjectStatus.ARCHIVED;
    const createdAt = formatCreatedAt(data.createdAt);
    const { street, locality } = splitAddress(data.formattedAddress);

    const handleProjectClick = () => {
      saveProjectSummary(data);
      router.push(`/projects/${data.id}`);
    };

    return (
      <section ref={ref} className={twMerge(cardClassName, className)} {...restProps}>
        <div
          role="button"
          onClick={handleProjectClick}
          className="flex h-full w-full cursor-pointer flex-col"
        >
          <ProjectCover
            seed={data.projectNumber || data.id}
            projectNumber={data.projectNumber}
            title={locality}
            subtitle={street}
          >
            {archived ? (
              <span className="absolute left-3.5 top-3.5 z-10 rounded bg-grey-mid px-2 py-0.5 text-xs font-semibold text-black shadow">
                Archived
              </span>
            ) : null}
            {badge ? (
              <span className="absolute right-3.5 top-3.5 z-10 inline-flex items-center gap-1 rounded bg-pink px-1.5 py-1 text-sm font-medium text-white shadow">
                <BellIcon />
                <span>{badge >= 100 ? '99+' : badge}</span>
              </span>
            ) : null}
          </ProjectCover>

          <div className="flex flex-1 flex-col gap-y-3 px-4 py-4">
            <div className="h-[42px]">
              <h3 className="truncate text-base font-medium leading-snug">
                {formatProjectName(data)}
              </h3>
              {createdAt ? (
                <p className="mt-0.5 truncate text-xs text-grey-dark">{createdAt}</p>
              ) : null}
            </div>

            <div className="h-px bg-grey-hover" />

            <div className="h-[66px]">
              <div className="flex items-baseline justify-between gap-x-2">
                <span className="text-sm leading-tight text-grey-dark">Contracts</span>
                <span className="shrink-0 text-sm font-semibold leading-tight">
                  {total === 1 ? '1 contract' : `${total} contracts`} · {executed} executed
                </span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1 overflow-hidden">
                {mix.map((label) => (
                  <span
                    key={label}
                    className="rounded border border-grey-mid bg-grey-light px-1.5 py-px text-xs font-medium text-grey-dark"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className="h-px bg-grey-hover" />

            {/*
              A direction with nothing claimed has no bar. `$0 of $0` over an empty
              track reads as an unfinished bar rather than "not applicable", which makes
              a perfectly healthy one-sided project look wrong.
            */}
            <div className="flex h-[54px] flex-col justify-center gap-y-0.5">
              {receiving.total > 0 ? (
                <ClaimsBar
                  label="Receiving"
                  paid={receiving.paid}
                  total={receiving.total}
                  ratio={receiving.ratio}
                  barClassName="bg-green"
                />
              ) : null}
              {paying.total > 0 ? (
                <ClaimsBar
                  label="Paying"
                  paid={paying.paid}
                  total={paying.total}
                  ratio={paying.ratio}
                  barClassName="bg-blue"
                />
              ) : null}
              {receiving.total === 0 && paying.total === 0 ? (
                <p className="text-sm leading-tight text-grey-dark">No claims yet</p>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    );
  },
);

ProjectCard.displayName = 'ProjectCard';
